import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Trash2, Search, Settings, Car, X, Check, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { carsApi, settingsApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useAdminLanguage } from '@/context/AdminLanguageContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminCars() {
  const { t } = useAdminLanguage();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [activeTab, setActiveTab] = useState('cars');
  
  // Settings state
  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoSaved, setAutoSaved] = useState(false);
  const saveTimeoutRef = useRef(null);

  useEffect(() => {
    loadCars();
    loadSettings();
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  const loadCars = async () => {
    try {
      const response = await carsApi.getAll({});
      setCars(response.data);
    } catch (error) {
      console.error('Error loading cars:', error);
      toast.error('Fehler beim Laden der Fahrzeuge');
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await settingsApi.get();
      setSettings(response.data);
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await carsApi.delete(deleteId);
      setCars(cars.filter(car => car.id !== deleteId));
      toast.success('Fahrzeug erfolgreich gelöscht');
    } catch (error) {
      toast.error('Fehler beim Löschen des Fahrzeugs');
    } finally {
      setDeleteId(null);
    }
  };

  // Auto-save settings
  const autoSave = async (newSettings) => {
    setSaving(true);
    try {
      await settingsApi.update(newSettings);
      setAutoSaved(true);
      setTimeout(() => setAutoSaved(false), 2000);
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleSettingChange = (field, value) => {
    const newSettings = { ...settings, [field]: value };
    setSettings(newSettings);
    
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      autoSave(newSettings);
    }, 1000);
  };

  const filteredCars = cars.filter(car =>
    car.brand.toLowerCase().includes(searchTerm.toLowerCase()) ||
    car.model.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
  };

  return (
    <div data-testid="admin-cars">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="font-heading text-3xl font-light">{t('cars_title')}</h1>
          <p className="text-[#666666] mt-1">{t('cars_manage_desc')}</p>
        </div>
        <Link to="/admin/cars/new" className="btn-primary inline-flex items-center gap-2" data-testid="add-car-btn">
          <Plus size={16} />
          {t('cars_add_new')}
        </Link>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-[#F9F9F7] p-1 rounded-lg">
          <TabsTrigger value="cars" className="flex items-center gap-2 data-[state=active]:bg-white">
            <Car size={16} />
            {t('cars_tab_list')}
          </TabsTrigger>
          <TabsTrigger value="categories" className="flex items-center gap-2 data-[state=active]:bg-white">
            <Settings size={16} />
            {t('cars_tab_categories')}
          </TabsTrigger>
        </TabsList>

        {/* Cars Tab */}
        <TabsContent value="cars" className="space-y-6">
          {/* Search */}
          <div className="bg-white border border-[#E5E5E5] p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#999999]" size={18} />
              <input
                type="text"
                placeholder={t('cars_search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] focus:outline-none"
                data-testid="cars-search"
              />
            </div>
          </div>

          {/* Table */}
          <div className="bg-white border border-[#E5E5E5] overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <div className="spinner mx-auto" />
              </div>
            ) : filteredCars.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-[#666666]">{t('cars_no_results')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full admin-table">
                  <thead>
                    <tr className="bg-[#F9F9F7]">
                      <th>{t('car_form_images') || 'Bild'}</th>
                      <th>{t('car_form_brand')}</th>
                      <th>{t('car_form_model')}</th>
                      <th>{t('car_form_price')}</th>
                      <th>{t('car_form_year')}</th>
                      <th>{t('car_form_fuel')}</th>
                      <th>{t('car_form_power')}</th>
                      <th>{t('car_form_status')}</th>
                      <th>{t('action_edit') || 'Aktionen'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCars.map((car) => (
                      <tr key={car.id}>
                        <td>
                          {car.images?.[0] ? (
                            <img
                              src={getImageUrl(car.images[0])}
                              alt={`${car.brand} ${car.model}`}
                              className="w-16 h-12 object-cover rounded"
                            />
                          ) : (
                            <div className="w-16 h-12 bg-[#E5E5E5] rounded flex items-center justify-center">
                              <Car size={20} className="text-[#999999]" />
                            </div>
                          )}
                        </td>
                        <td className="font-medium">{car.brand}</td>
                        <td>{car.model}</td>
                        <td>€{car.price?.toLocaleString('de-DE')}</td>
                        <td>{car.year}</td>
                        <td>{car.fuel_type}</td>
                        <td>{car.horsepower} PS</td>
                        <td>
                          <div className="flex flex-col gap-1">
                            <span className={car.is_active ? 'status-active' : 'status-inactive'}>
                              {car.is_active ? t('status_active') : t('status_inactive')}
                            </span>
                            {car.is_sold && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-700 rounded-full w-fit">
                                {t('status_sold')}
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            <Link
                              to={`/admin/cars/${car.id}/edit`}
                              className="p-2 hover:bg-[#F9F9F7] rounded"
                              title={t('cars_edit')}
                            >
                              <Pencil size={16} />
                            </Link>
                            <button
                              onClick={() => setDeleteId(car.id)}
                              className="p-2 hover:bg-red-50 text-red-500 rounded"
                              title={t('cars_delete')}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories" className="space-y-6">
          {settingsLoading ? (
            <div className="p-8 text-center">
              <div className="spinner mx-auto" />
            </div>
          ) : settings ? (
            <>
              {/* Save Status */}
              <div className="flex items-center justify-end gap-2">
                {saving && (
                  <span className="text-sm text-[#666666] flex items-center gap-2">
                    <RefreshCw size={16} className="animate-spin" />
                    {t('msg_loading') || 'Speichern...'}
                  </span>
                )}
                {autoSaved && !saving && (
                  <span className="text-sm text-green-600 flex items-center gap-2">
                    <Check size={16} />
                    {t('msg_save_success')}
                  </span>
                )}
              </div>

              <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
                <h3 className="font-heading text-lg mb-2">{t('cars_tab_categories') === 'Categories' ? 'Manage Vehicle Categories' : 'Fahrzeug-Kategorien verwalten'}</h3>
                <p className="text-sm text-[#666666]">
                  {t('cars_tab_categories') === 'Categories' ? 'Define available options for fuel, transmission, drivetrain and colors.' : 'Definieren Sie die verfügbaren Optionen für Kraftstoff, Getriebe, Antrieb und Farben.'}
                </p>
              </div>

              {/* Fuel Types */}
              <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
                <h3 className="font-heading text-lg mb-4">{t('car_form_fuel') === 'Fuel Type' ? 'Fuel Types' : 'Kraftstoff-Typen'}</h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  {(settings.car_fuel_types || ['Benzin', 'Diesel', 'Elektro', 'Hybrid', 'Plugin-Hybrid', 'Wasserstoff', 'Erdgas']).map((type, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded text-blue-800"
                    >
                      {type}
                      <button
                        type="button"
                        onClick={() => {
                          const newTypes = (settings.car_fuel_types || ['Benzin', 'Diesel', 'Elektro', 'Hybrid', 'Plugin-Hybrid', 'Wasserstoff', 'Erdgas']).filter((_, i) => i !== index);
                          handleSettingChange('car_fuel_types', newTypes);
                        }}
                        className="text-blue-500 hover:text-red-500"
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Neuer Kraftstoff-Typ"
                    className="input-premium flex-1"
                    id="new-fuel-type"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const value = e.target.value.trim();
                        if (value) {
                          const currentTypes = settings.car_fuel_types || ['Benzin', 'Diesel', 'Elektro', 'Hybrid', 'Plugin-Hybrid', 'Wasserstoff', 'Erdgas'];
                          if (!currentTypes.includes(value)) {
                            handleSettingChange('car_fuel_types', [...currentTypes, value]);
                          }
                          e.target.value = '';
                        }
                      }
                    }}
                  />
                  <Button
                    type="button"
                    onClick={() => {
                      const input = document.getElementById('new-fuel-type');
                      const value = input.value.trim();
                      if (value) {
                        const currentTypes = settings.car_fuel_types || ['Benzin', 'Diesel', 'Elektro', 'Hybrid', 'Plugin-Hybrid', 'Wasserstoff', 'Erdgas'];
                        if (!currentTypes.includes(value)) {
                          handleSettingChange('car_fuel_types', [...currentTypes, value]);
                        }
                        input.value = '';
                      }
                    }}
                    className="btn-secondary flex items-center gap-2"
                  >
                    <Plus size={16} />
                    {t('action_add')}
                  </Button>
                </div>
              </div>

              {/* Transmissions */}
              <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
                <h3 className="font-heading text-lg mb-4">{t('car_form_transmission') === 'Transmission' ? 'Transmission Types' : 'Getriebe-Typen'}</h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  {(settings.car_transmissions || ['Automatik', 'Schaltgetriebe', 'DSG', 'CVT']).map((type, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded text-purple-800"
                    >
                      {type}
                      <button
                        type="button"
                        onClick={() => {
                          const newTypes = (settings.car_transmissions || ['Automatik', 'Schaltgetriebe', 'DSG', 'CVT']).filter((_, i) => i !== index);
                          handleSettingChange('car_transmissions', newTypes);
                        }}
                        className="text-purple-500 hover:text-red-500"
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Neuer Getriebe-Typ"
                    className="input-premium flex-1"
                    id="new-transmission"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const value = e.target.value.trim();
                        if (value) {
                          const currentTypes = settings.car_transmissions || ['Automatik', 'Schaltgetriebe', 'DSG', 'CVT'];
                          if (!currentTypes.includes(value)) {
                            handleSettingChange('car_transmissions', [...currentTypes, value]);
                          }
                          e.target.value = '';
                        }
                      }
                    }}
                  />
                  <Button
                    type="button"
                    onClick={() => {
                      const input = document.getElementById('new-transmission');
                      const value = input.value.trim();
                      if (value) {
                        const currentTypes = settings.car_transmissions || ['Automatik', 'Schaltgetriebe', 'DSG', 'CVT'];
                        if (!currentTypes.includes(value)) {
                          handleSettingChange('car_transmissions', [...currentTypes, value]);
                        }
                        input.value = '';
                      }
                    }}
                    className="btn-secondary flex items-center gap-2"
                  >
                    <Plus size={16} />
                    {t('action_add')}
                  </Button>
                </div>
              </div>

              {/* Drivetrains */}
              <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
                <h3 className="font-heading text-lg mb-4">{t('car_form_transmission') === 'Transmission' ? 'Drivetrains' : 'Antriebsarten'}</h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  {(settings.car_drivetrains || ['Frontantrieb', 'Heckantrieb', 'Allrad', '4x4']).map((type, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded text-green-800"
                    >
                      {type}
                      <button
                        type="button"
                        onClick={() => {
                          const newTypes = (settings.car_drivetrains || ['Frontantrieb', 'Heckantrieb', 'Allrad', '4x4']).filter((_, i) => i !== index);
                          handleSettingChange('car_drivetrains', newTypes);
                        }}
                        className="text-green-500 hover:text-red-500"
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Neue Antriebsart"
                    className="input-premium flex-1"
                    id="new-drivetrain"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const value = e.target.value.trim();
                        if (value) {
                          const currentTypes = settings.car_drivetrains || ['Frontantrieb', 'Heckantrieb', 'Allrad', '4x4'];
                          if (!currentTypes.includes(value)) {
                            handleSettingChange('car_drivetrains', [...currentTypes, value]);
                          }
                          e.target.value = '';
                        }
                      }
                    }}
                  />
                  <Button
                    type="button"
                    onClick={() => {
                      const input = document.getElementById('new-drivetrain');
                      const value = input.value.trim();
                      if (value) {
                        const currentTypes = settings.car_drivetrains || ['Frontantrieb', 'Heckantrieb', 'Allrad', '4x4'];
                        if (!currentTypes.includes(value)) {
                          handleSettingChange('car_drivetrains', [...currentTypes, value]);
                        }
                        input.value = '';
                      }
                    }}
                    className="btn-secondary flex items-center gap-2"
                  >
                    <Plus size={16} />
                    {t('action_add')}
                  </Button>
                </div>
              </div>

              {/* Colors */}
              <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
                <h3 className="font-heading text-lg mb-4">{t('car_form_color') === 'Color' ? 'Colors' : 'Farben'}</h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  {(settings.car_colors || ['Schwarz', 'Weiß', 'Silber', 'Grau', 'Blau', 'Rot', 'Grün', 'Gelb', 'Orange', 'Braun', 'Beige', 'Gold', 'Bronze']).map((color, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded text-amber-800"
                    >
                      {color}
                      <button
                        type="button"
                        onClick={() => {
                          const newColors = (settings.car_colors || ['Schwarz', 'Weiß', 'Silber', 'Grau', 'Blau', 'Rot', 'Grün', 'Gelb', 'Orange', 'Braun', 'Beige', 'Gold', 'Bronze']).filter((_, i) => i !== index);
                          handleSettingChange('car_colors', newColors);
                        }}
                        className="text-amber-500 hover:text-red-500"
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Neue Farbe"
                    className="input-premium flex-1"
                    id="new-color"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const value = e.target.value.trim();
                        if (value) {
                          const currentColors = settings.car_colors || ['Schwarz', 'Weiß', 'Silber', 'Grau', 'Blau', 'Rot', 'Grün', 'Gelb', 'Orange', 'Braun', 'Beige', 'Gold', 'Bronze'];
                          if (!currentColors.includes(value)) {
                            handleSettingChange('car_colors', [...currentColors, value]);
                          }
                          e.target.value = '';
                        }
                      }
                    }}
                  />
                  <Button
                    type="button"
                    onClick={() => {
                      const input = document.getElementById('new-color');
                      const value = input.value.trim();
                      if (value) {
                        const currentColors = settings.car_colors || ['Schwarz', 'Weiß', 'Silber', 'Grau', 'Blau', 'Rot', 'Grün', 'Gelb', 'Orange', 'Braun', 'Beige', 'Gold', 'Bronze'];
                        if (!currentColors.includes(value)) {
                          handleSettingChange('car_colors', [...currentColors, value]);
                        }
                        input.value = '';
                      }
                    }}
                    className="btn-secondary flex items-center gap-2"
                  >
                    <Plus size={16} />
                    {t('action_add')}
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="p-8 text-center text-[#666666]">
              {t('msg_no_data') || 'Einstellungen konnten nicht geladen werden.'}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('cars_delete') === 'Delete' ? 'Delete Vehicle' : 'Fahrzeug löschen'}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('cars_delete') === 'Delete' ? 'Are you sure you want to delete this vehicle? This action cannot be undone.' : 'Sind Sie sicher, dass Sie dieses Fahrzeug löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('action_cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              {t('action_delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
